/**
 * DFT-0044 (FE-13/FE-70/FE-74): shared file-format/size validation for the two upload surfaces
 * that still use a raw `<input type="file">` rather than the shared `FileUpload` dropzone -
 * `PreApproveDialog`'s HOD document and `ReceiptUploadDialog`'s receipt - so both are held to the
 * same rule the create form's own `BayHireCreateAttachmentUpload` already enforces via
 * `FileUpload.constants.ts`'s `bayHireAttachment` profile (DFT-0088 addendum, 2026-09-01:
 * pdf/jpg/jpeg/png/bmp/gif plus Excel/Word, 10 MB - widened from the original pdf/jpg/png-only
 * rule), rather than each carrying its own (looser, unenforced-size) copy of the rule.
 *
 * Extension-based, matching `FileUpload.tsx`'s own `isAllowedType` - not MIME-type-based, which is
 * what both call sites checked before. Browsers report inconsistent/absent MIME types for the same
 * file depending on OS and how it arrived (e.g. a `.pdf` dragged from some sources reports
 * `application/octet-stream`), so an extension check is both stricter and more reliable.
 */

export interface BayHireFileValidationResult {
  ok: boolean;
  reason?: "type" | "size";
}

const extensionOf = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex === -1 ? "" : fileName.slice(dotIndex + 1).toLowerCase();
};

export const validateBayHireAttachmentFile = (
  file: File,
  conditions: { format?: string[]; maxSizeBytes?: number },
): BayHireFileValidationResult => {
  const allowedExtensions = (conditions.format ?? []).map((ext) => ext.toLowerCase());
  if (allowedExtensions.length > 0 && !allowedExtensions.includes(extensionOf(file.name))) {
    return { ok: false, reason: "type" };
  }
  if (conditions.maxSizeBytes && file.size > conditions.maxSizeBytes) {
    return { ok: false, reason: "size" };
  }
  return { ok: true };
};
